/**
 * useCoworkSession (mobile) — 把 dispatch relay 的事件流 fold 成会话 transcript。
 *
 * 和 desktop renderer 跑同一个 @cowork/streamDecoder：
 *   - cowork-message 事件里的原生 SDK 消息 → decodeStreamMessage → 写进 store
 *   - snapshot 事件 → 拆出 SDK 消息历史，同样喂 decoder
 *   - dispatch 专属事件（task_new / user_message / control / file-artifact /
 *     permission）→ 本 driver 直接翻成 store mutation / 本地 state
 *
 * decoder 是被外部驱动的：driver 负责 turn 生命周期 —— 何时新建 assistant
 * 消息、维护 assistantMsgId、按 SDK uuid 去重。
 */

import { useEffect, useRef, useState } from 'react'

import { decodeStreamMessage, type StreamDecoderContext } from '@cowork/streamDecoder'
import { createSessionRuntimeRegistry } from '@cowork/sessionRuntime'
import type { CortexMessage, CortexMessageFile, HITLState } from '@cowork/sessionModel'

import { useSessionStore } from '../stores/sessionStore'
import {
  getSessionEvents,
  postPermissionDecision,
  type EventOut,
  type RequiresActionDetail,
} from '../lib/api'
import { openSse, type SseHandle } from '../lib/sse'
import { clearToken } from '../lib/auth'
import { useLang } from '../contexts/LanguageContext'

export type DispatchSessionState =
  | 'idle' | 'processing' | 'streaming' | 'awaiting_user' | 'completed' | 'errored' | 'paused'

export interface CoworkSessionView {
  messages: CortexMessage[]
  state: DispatchSessionState
  requiresAction: RequiresActionDetail | null
  sseConnected: boolean
  loaded: boolean
  /** deck 已发 request_replay，snapshot 经 SSE tail 异步到 —— UI 显示"加载历史中"。 */
  replayPending: boolean
  error: string | null
  /** 裁决权限请求 —— 上报 deck + 乐观 dismiss 卡片。 */
  resolvePermission: (decision: 'allow' | 'deny' | 'allow_always') => Promise<void>
}

const EMPTY: CortexMessage[] = []

let _seq = 0
function newId(prefix: string): string {
  _seq += 1
  return `${prefix}-${Date.now().toString(36)}-${_seq}`
}

export function useCoworkSession(
  sessionId: string | undefined,
  onAuthExpired: () => void,
): CoworkSessionView {
  const { t, lang } = useLang()

  const messages = useSessionStore((s) => (sessionId ? s.sessions[sessionId]?.messages : undefined) ?? EMPTY)
  const phase = useSessionStore((s) => (sessionId ? s.sessions[sessionId]?.streamingPhase : undefined))

  const [controlState, setControlState] = useState<DispatchSessionState>('idle')
  const [requiresAction, setRequiresAction] = useState<RequiresActionDetail | null>(null)
  const [sseConnected, setSseConnected] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [replayPending, setReplayPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ── per-driver mutable state（不进 render） ──
  const runtimes = useRef(createSessionRuntimeRegistry())
  const hitlQueue = useRef<Array<{ hitl: HITLState; nonce: string }>>([])
  const seenStreamIds = useRef<Set<string>>(new Set())
  const seenUuids = useRef<Set<string>>(new Set())
  const turn = useRef<{ assistantMsgId: string | null; active: boolean }>({ assistantMsgId: null, active: false })
  // ingest 逻辑放 ref — SSE 订阅只建一次，但闭包要拿到最新的 t / lang。
  const ingestRef = useRef<(ev: EventOut) => void>(() => {})

  // ── decoder ctx（每次 decode 现搭，复用稳定的 ref；闭包带最新 t/lang） ──
  function buildCtx(): StreamDecoderContext {
    const store = useSessionStore
    return {
      store,
      runtimes: runtimes.current,
      hitlQueueRef: hitlQueue,
      appendText: (s, msgId, text) => store.getState().updateMessages(s, (prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, content: m.content + text } : m))),
      batchUpdate: (s, msgId, opts) => store.getState().updateMessages(s, (prev) =>
        prev.map((m) => {
          if (m.id !== msgId) return m
          let next: CortexMessage = opts.patch ? { ...m, ...opts.patch } : m
          if (opts.blocks) next = { ...next, contentBlocks: opts.blocks(next.contentBlocks ?? []) }
          return next
        })),
      friendlyToolName: (name) => name,
      computeToolLabel: (name) => name,
      t,
      lang,
      api: { respondAskUser: () => { /* P5: route ask_user_question back to desktop */ } },
      backgroundEvent: ({ kind }) => {
        if (typeof document === 'undefined' || document.hasFocus() || typeof Notification === 'undefined') return
        const body = kind === 'input-needed'
          ? t('desktop.cowork.inputNeeded')
          : kind === 'failed'
            ? t('desktop.cowork.taskError')
            : t('desktop.cowork.taskComplete')
        const notification = new Notification('Cortex Cowork', { body })
        notification.onclick = () => window.focus()
      },
      loadConversations: async () => {},
      setHitl: (v) => store.getState().setHitl(v),
      drainPending: () => {},
      notify: (level, text) => console.log(`[cowork:${level}]`, text),
    }
  }

  // ── 一条原生 SDK 消息 → decoder ──
  function feedSdkMessage(sid: string, msg: unknown): void {
    const uuid = (msg as { uuid?: string })?.uuid
    if (uuid) {
      if (seenUuids.current.has(uuid)) return
      seenUuids.current.add(uuid)
    }
    const mtype = (msg as { type?: string })?.type
    // turn start：本 turn 第一条 assistant 内容 → 新建 assistant 消息
    if ((mtype === 'assistant' || mtype === 'stream_event') && !turn.current.active) {
      const id = newId('a')
      useSessionStore.getState().updateMessages(sid, (prev) => [...prev, {
        id, role: 'assistant', content: '', status: 'streaming',
        createdAt: new Date(), contentBlocks: [],
      }])
      turn.current = { assistantMsgId: id, active: true }
    }
    try {
      decodeStreamMessage(msg, turn.current.assistantMsgId ?? '', sid, buildCtx())
    } catch (e) {
      console.warn('[cowork] decode error', e)
    }
    if (mtype === 'result') turn.current = { assistantMsgId: null, active: false }
  }

  // ── 往 transcript 追加一条用户消息（task_new / user_message / voice echo） ──
  function appendUserMessage(sid: string, id: string, text: string): void {
    useSessionStore.getState().updateMessages(sid, (prev) => {
      if (prev.some((m) => m.id === id)) return prev
      return [...prev, {
        id, role: 'user', content: text, status: 'success', createdAt: new Date(),
      }]
    })
  }

  // ── 往 transcript 追加一条文件消息（桌面产物 / 用户附件） ──
  // 复用 CortexMessage.files（CortexMessageFile[]）—— 不污染 cowork-core
  // 的 SDK 会话模型。type='artifact' 桌面产物 / 'upload' 用户附件。
  function appendFileMessage(sid: string, id: string, files: CortexMessageFile[]): void {
    useSessionStore.getState().updateMessages(sid, (prev) => {
      if (prev.some((m) => m.id === id)) return prev
      return [...prev, {
        id, role: 'assistant', content: '', status: 'success', createdAt: new Date(), files,
      }]
    })
  }

  // ── dispatch relay 事件分流 ──
  function ingestEvent(sid: string, ev: EventOut): void {
    if (seenStreamIds.current.has(ev.stream_id)) return
    seenStreamIds.current.add(ev.stream_id)

    switch (ev.type) {
      case 'cowork-message': {
        const m = (ev.payload as { message?: unknown }).message
        if (m) feedSdkMessage(sid, m)
        break
      }
      case 'snapshot': {
        const msgs = (ev.payload as { messages?: unknown[] }).messages ?? []
        for (const m of msgs) feedSdkMessage(sid, m)
        break
      }
      case 'control': {
        const p = ev.payload as {
          state?: DispatchSessionState
          user_echo?: { message_id: string; text: string }
        }
        if (p.state) setControlState(p.state)
        if (p.user_echo?.text) appendUserMessage(sid, `u-${p.user_echo.message_id}`, p.user_echo.text)
        break
      }
      case 'task_new':
      case 'user_message': {
        const p = ev.payload as {
          content?: string; prompt?: string; message_id?: string
          attachments?: Array<{ upload_id: string; kind?: string }>
        }
        const text = p.content ?? p.prompt ?? ''
        const baseId = p.message_id ?? ev.stream_id
        const atts = p.attachments ?? []
        if (atts.length > 0) {
          appendFileMessage(sid, `att-${baseId}`, atts.map((a) => ({
            id: a.upload_id, filename: '', mime_type: '', format: a.kind ?? 'file', type: 'upload',
          })))
        }
        if (text) appendUserMessage(sid, `u-${baseId}`, text)
        break
      }
      case 'file-artifact': {
        const p = ev.payload as { upload_id?: string; filename?: string; kind?: string; size?: number }
        if (p.upload_id) {
          appendFileMessage(sid, `fa-${ev.stream_id}`, [{
            id: p.upload_id, filename: p.filename ?? '', mime_type: '',
            format: p.kind ?? 'file', type: 'artifact',
          }])
        }
        break
      }
      case 'permission': {
        const p = ev.payload as {
          phase?: string; request_id?: string; tool_use_id?: string; tool_name?: string
          display_tool_name?: string; action_description?: string; raw_command?: string
        }
        if (p.phase === 'requested' && p.request_id) {
          setRequiresAction({
            request_id: p.request_id,
            tool_use_id: p.tool_use_id ?? '',
            tool_name: p.tool_name ?? '',
            display_tool_name: p.display_tool_name,
            action_description: p.action_description ?? '',
            raw_command: p.raw_command,
          })
          setControlState('awaiting_user')
        } else if (p.phase === 'resolved') {
          setRequiresAction(null)
        }
        break
      }
    }
  }

  ingestRef.current = (ev) => { if (sessionId) ingestEvent(sessionId, ev) }

  // ── 进入会话：reset → snapshot catch-up → SSE tail ──
  useEffect(() => {
    if (!sessionId) return
    const sid = sessionId
    useSessionStore.getState().resetSession(sid)
    runtimes.current = createSessionRuntimeRegistry()
    seenStreamIds.current = new Set()
    seenUuids.current = new Set()
    turn.current = { assistantMsgId: null, active: false }
    setControlState('idle')
    setRequiresAction(null)
    setLoaded(false)
    setReplayPending(false)
    setError(null)

    let sse: SseHandle | null = null
    let alive = true

    getSessionEvents(sid, '0', 200)
      .then((res) => {
        if (!alive) return
        for (const ev of res.events) ingestRef.current(ev)
        setReplayPending(res.replay_pending ?? false)
        setLoaded(true)
        sse = openSse({
          path: `/dispatch/sessions/${sid}/events/stream`,
          initialSince: res.next_since ?? '0',
          onEvent: (ev) => ingestRef.current(ev as EventOut),
          onConnected: () => setSseConnected(true),
          onDisconnected: () => setSseConnected(false),
        })
      })
      .catch((e: Error) => {
        if (!alive) return
        if ((e as { status?: number }).status === 401) {
          clearToken()
          onAuthExpired()
          return
        }
        setError(e?.message?.slice(0, 200) || t('common.loadFailed'))
        setLoaded(true)
      })

    return () => {
      alive = false
      sse?.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  // ── transcript 推导出的会话状态 —— control 事件只覆盖 pre-SDK 那段空窗 ──
  const lastStatus = messages[messages.length - 1]?.status
  let state: DispatchSessionState = controlState
  if (lastStatus === 'streaming' || (phase && phase !== 'idle')) state = 'processing'
  else if (lastStatus === 'error') state = 'errored'
  else if (messages.length > 0 && controlState !== 'errored' && controlState !== 'awaiting_user') state = 'idle'

  const resolvePermission = async (decision: 'allow' | 'deny' | 'allow_always'): Promise<void> => {
    if (!sessionId || !requiresAction) return
    const reqId = requiresAction.request_id
    setRequiresAction(null) // 乐观 dismiss
    try {
      await postPermissionDecision(sessionId, reqId, decision)
    } catch (e) {
      console.warn('[cowork] permission decision failed', e)
    }
  }

  return { messages, state, requiresAction, sseConnected, loaded, replayPending, error, resolvePermission }
}
